import { useState, useMemo } from 'react';
import { LuUpload, LuX, LuFileText, LuImage, LuTrash2 } from 'react-icons/lu';
import { toast } from 'sonner';

export default function EditFaultForm({ fault, existingAttachments, onSubmit, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: fault.title || '',
    description: fault.description || '',
    category: fault.category || 'Other',
    location: fault.location || '',
    priority: fault.priority || 'Medium',
    is_anonymous: fault.is_anonymous || false
  });

  const [attachments, setAttachments] = useState(existingAttachments || []);
  const [newAttachments, setNewAttachments] = useState([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState([]);
  const [uploading, setUploading] = useState(false);

  const categories = ['Electrical', 'Plumbing', 'Elevator', 'Painting', 'Landscaping', 'Security', 'Other'];
  const priorities = ['Low', 'Medium', 'High', 'Urgent'];

  const isImage = (fileName) => /\.(jpg|jpeg|png|webp|avif|gif|svg)$/i.test(fileName);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (attachments.length + newAttachments.length + files.length > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }
    setUploading(true);
    try {
      const uploadedFiles = await Promise.all(
        files.map(async (file) => {
          const { data: fileId, error } = await window.ezsite.apis.upload({ filename: file.name, file: file });
          if (error) throw error;
          const { data: res, error: urlError } = await window.ezsite.apis.getUploadUrl(fileId);
          if (urlError) throw urlError;
          const urlString = typeof res === 'string' ? res : res?.url;
          return { fileId, url: urlString, name: file.name };
        })
      );
      setNewAttachments((prev) => [...prev, ...uploadedFiles]);
      toast.success(`${files.length} file(s) uploaded`);
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const removeExistingAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    setDeletedAttachmentIds((prev) => [...prev, id]);
  };

  const removeNewAttachment = (index) => {
    setNewAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setLoading(true);
    try {
      await onSubmit(formData, newAttachments, deletedAttachmentIds);
    } catch (error) {
      console.error('Update failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-bold text-gray-700 mb-2">Issue Title <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="What is the issue?" />

        </div>

        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">Category</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none">

            {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">Priority</label>
          <select
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none">

            {priorities.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-bold text-gray-700 mb-2">Specific Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none"
            placeholder="Room number, floor, or area..." />

        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none resize-none"
            placeholder="Provide more details about the issue..." />

        </div>
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-bold text-gray-700">Attachments (Max 5 Total)</label>
        
        {/* Existing Attachments */}
        {attachments.length > 0 &&
        <div className="grid grid-cols-1 gap-2">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Current Files</p>
            {attachments.map((file) =>
          <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  {isImage(file.file_name || file.file_url) ? <LuImage className="text-blue-500" /> : <LuFileText className="text-orange-500" />}
                  <span className="text-sm font-bold text-gray-700 truncate max-w-[200px]">{file.file_name || 'Attachment'}</span>
                </div>
                <button
              type="button"
              onClick={() => removeExistingAttachment(file.id)}
              className="p-2 hover:bg-red-50 rounded-lg text-red-400 transition-colors">

                  <LuTrash2 className="w-4 h-4" />
                </button>
              </div>
          )}
          </div>
        }

        {/* New Attachments */}
        {newAttachments.length > 0 &&
        <div className="grid grid-cols-1 gap-2">
            <p className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">Added Files</p>
            {newAttachments.map((file, index) =>
          <div key={index} className="flex items-center justify-between p-3 bg-blue-50/50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-3">
                  {isImage(file.name) ? <LuImage className="text-blue-500" /> : <LuFileText className="text-orange-500" />}
                  <span className="text-sm font-bold text-gray-700 truncate max-w-[200px]">{file.name}</span>
                </div>
                <button
              type="button"
              onClick={() => removeNewAttachment(index)}
              className="p-2 hover:bg-red-50 rounded-lg text-red-400 transition-colors">

                  <LuX className="w-4 h-4" />
                </button>
              </div>
          )}
          </div>
        }

        {attachments.length + newAttachments.length < 5 &&
        <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 text-center hover:border-blue-400 hover:bg-blue-50/30 transition-all cursor-pointer relative group">
            <input
            type="file"
            multiple
            onChange={handleFileSelect}
            className="absolute inset-0 opacity-0 cursor-pointer"
            disabled={uploading} />

            <LuUpload className="w-8 h-8 text-gray-300 mx-auto mb-2 group-hover:text-blue-400 transition-colors" />
            <p className="text-sm font-bold text-gray-500">
              {uploading ? 'Uploading...' : 'Tap to add more files'}
            </p>
          </div>
        }
      </div>

      <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-6 py-4 border border-gray-200 rounded-2xl font-bold text-gray-600 hover:bg-gray-50 transition-all">

          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || uploading}
          className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all disabled:opacity-50">

          {loading ? 'Saving Changes...' : 'Update Report'}
        </button>
      </div>
    </form>);

}